import { useEffect, useRef, useState } from 'react';

const features = [
  {
    icon: 'ri-map-pin-line',
    title: 'Local Expertise & Perfect Fit',
    description: 'Years of experience serving the Hendala community with personalized attention to every detail.'
  },
  {
    icon: 'ri-star-line',
    title: 'Quality Fabrics That Last',
    description: 'We source only the finest materials to ensure your garments stand the test of time.'
  },
  {
    icon: 'ri-price-tag-3-line',
    title: 'Honest, Upfront Pricing',
    description: 'Transparent pricing with no hidden costs. Quality tailoring at fair, competitive rates.'
  }
];

export default function About() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-32 px-6 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/5 via-transparent to-amber-950/5 pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto relative">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-block px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full mb-6">
            <span className="text-xs uppercase tracking-[0.2em] text-amber-400 font-medium">
              Our Story
            </span>
          </div>
          <h2 className="text-5xl font-bold mb-6 tracking-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Why Rinard Tailors
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Story */}
          <div className={`space-y-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <p className="text-lg text-gray-300 leading-relaxed">
              Rinard Tailors is a family-run tailoring shop nestled in the heart of Hendala, Wattala. 
              With years of dedicated experience in the art of bespoke tailoring, we've built our reputation 
              on precision, quality, and an unwavering commitment to customer satisfaction.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed">
              Every suit, coat, and shirt that leaves our workshop is a testament to our passion for 
              craftsmanship. We believe that the perfect fit isn't just about measurements—it's about 
              understanding your style, your needs, and your vision.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed">
              From weddings to boardrooms, from casual elegance to formal sophistication, we're here 
              to ensure you look and feel your absolute best.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-amber-400 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>32+</div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-amber-400 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>200K+</div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">Happy Clients</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-amber-400 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>100%</div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">Satisfaction</div>
              </div>
            </div>
          </div>

          {/* Right - Features */}
          <div className={`space-y-6 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            {features.map((feature, index) => (
              <div
                key={index}
                className="group p-6 bg-gradient-to-br from-gray-900 to-gray-950 rounded-2xl border border-gray-800 hover:border-amber-500/50 transition-all duration-300"
              >
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center shadow-lg shadow-amber-500/20 group-hover:shadow-amber-500/40 transition-all duration-300">
                      <i className={`${feature.icon} text-xl text-black`}></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2 text-white group-hover:text-amber-400 transition-colors" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {feature.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
